import { Link } from 'react-router-dom';
import { Menu, Heart, User, X, ChevronDown } from 'lucide-react';
import { UserMenu } from './components/user-menu';
import { CartDrawer } from '../cart-drawer/cart-drawer';
import { SearchDrawer } from '../search-drawer/search-drawer';
import { cn } from '@/lib/utils';
import { Category } from '@/entities/category.entity';
import { useWishlist } from '@/contexts/wishlist-context';
import { usePresetCategories } from '@/contexts/preset-categories-context';
import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';

interface NavBarProps {
  categories?: Category[];
}

// Logo Component
const Logo = ({ className }: { className?: string }) => {
  return (
    <Link to="/" className={cn(['text-xl font-medium tracking-tight', className])}>
      STORE
    </Link>
  );
};

const NavIconButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { badge?: number }
>(({ children, className, badge, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={cn([
        'relative p-2 text-foreground/80 hover:text-foreground transition-colors duration-200',
        className,
      ])}
      {...props}
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 flex items-center justify-center rounded-full bg-foreground text-background text-[10px] font-medium">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  );
});
NavIconButton.displayName = 'NavIconButton';

// Wishlist Button
const WishlistButton = () => {
  const { wishlistCount } = useWishlist();
  return (
    <Link
      to="/wishlist"
      className="relative p-2 text-foreground/80 hover:text-foreground transition-colors duration-200"
    >
      <Heart className="h-5 w-5" strokeWidth={1.5} />
      {wishlistCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 flex items-center justify-center rounded-full bg-foreground text-background text-[10px] font-medium">
          {wishlistCount > 9 ? '9+' : wishlistCount}
        </span>
      )}
    </Link>
  );
};

// Mega Menu Content Component
const MegaMenuContent = ({ category }: { category: Category }) => {
  const [activeSubCategory, setActiveSubCategory] = useState<Category | null>(
    category.children?.[0] || null
  );

  return (
    <div className="flex w-[650px] bg-background rounded-lg shadow-lg border border-border/50 overflow-hidden">
      {/* Left Panel - Subcategories */}
      <div className="w-[220px] bg-muted/30 py-3">
        <div className="px-4 pb-2 mb-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Categories
          </span>
        </div>
        {category.children?.map((subCategory) => (
          <Link
            key={subCategory.id}
            to={`/categories/${subCategory.id}`}
            className={cn(
              'flex items-center justify-between px-4 py-2.5 text-sm transition-all duration-150',
              activeSubCategory?.id === subCategory.id
                ? 'bg-background text-foreground font-medium shadow-sm border-l-2 border-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
            )}
            onMouseEnter={() => setActiveSubCategory(subCategory)}
          >
            <span>{subCategory.name}</span>
            {subCategory.children && subCategory.children.length > 0 && (
              <ChevronDown className="w-3.5 h-3.5 -rotate-90 opacity-50" />
            )}
          </Link>
        ))}

        {/* View All Link */}
        <div className="px-4 pt-3 mt-2 border-t border-border/50">
          <Link
            to={`/categories/${category.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:underline"
          >
            View All {category.name}
            <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
          </Link>
        </div>
      </div>

      {/* Right Panel - Leaf Categories */}
      <div className="flex-1 p-6 bg-background">
        {activeSubCategory && (
          <>
            <Link
              to={`/categories/${activeSubCategory.id}`}
              className="inline-flex items-center gap-2 text-base font-semibold text-foreground hover:text-primary transition-colors mb-5 group"
            >
              {activeSubCategory.name}
              <ChevronDown className="w-4 h-4 -rotate-90 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </Link>

            {activeSubCategory.children && activeSubCategory.children.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-10 gap-y-1">
                {activeSubCategory.children.map((leafCategory) => (
                  <Link
                    key={leafCategory.id}
                    to={`/categories/${leafCategory.id}`}
                    className="text-sm text-muted-foreground hover:text-foreground py-1.5 transition-colors hover:translate-x-0.5 duration-150"
                  >
                    {leafCategory.name}
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Explore our {activeSubCategory.name.toLowerCase()} collection
              </p>
            )}

            {/* Shop All Button */}
            <div className="mt-6 pt-4 border-t border-border/50">
              <Link
                to={`/categories/${activeSubCategory.id}`}
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                Shop all {activeSubCategory.name}
                <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Navigation Links for Desktop
const DesktopNav = ({ categories }: { categories: Category[] }) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Check if we're in preview mode
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkPreviewMode = () => {
        setIsPreviewMode(sessionStorage.getItem('is-preview-mode') === 'true');
      };
      checkPreviewMode();
      // Listen for storage changes
      window.addEventListener('storage', checkPreviewMode);
      return () => window.removeEventListener('storage', checkPreviewMode);
    }
  }, []);

  if (!Array.isArray(categories)) {
    return null;
  }

  return (
    <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {categories.map((category) => {
          if (isPreviewMode) {
            return (
              <div
                key={category.id}
                className="px-4 py-2 text-sm font-medium text-foreground/70 cursor-default"
              >
                {category.name}
              </div>
            );
          }

          if (!category.children?.length) {
            return (
              <NavigationMenuItem key={category.id}>
                <NavigationMenuLink asChild>
                  <Link to={`/categories/${category.id}`} className="px-4 py-2 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors">
                    {category.name}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            );
          }

          return (
            <NavigationMenuItem key={category.id}>
              <NavigationMenuTrigger className="px-4 py-2 text-sm font-medium text-foreground/70 hover:text-foreground bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent">
                {category.name}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <MegaMenuContent category={category} />
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
};
// Mobile Navigation
const MobileNav = ({ categories }: { categories: Category[] }) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Check if we're in preview mode
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkPreviewMode = () => {
        setIsPreviewMode(sessionStorage.getItem('is-preview-mode') === 'true');
      };
      checkPreviewMode();
      // Listen for storage changes
      window.addEventListener('storage', checkPreviewMode);
      return () => window.removeEventListener('storage', checkPreviewMode);
    }
  }, []);

  if (!Array.isArray(categories)) {
    return <div className="w-10" />; // Placeholder for consistent spacing
  }

  const toggleCategory = (categoryId: string) => {
    if (isPreviewMode) return; // Don't expand in preview mode
    setExpandedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <NavIconButton className="lg:hidden">
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </NavIconButton>
      </SheetTrigger>
      <SheetContent side="left" className="w-full sm:max-w-md p-0">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <Logo />
            <SheetClose asChild>
              <NavIconButton>
                <X className="h-5 w-5" strokeWidth={1.5} />
              </NavIconButton>
            </SheetClose>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6">
            <ul className="space-y-1">
              {categories.map((category) => (
                <li key={category.id}>
                  {isPreviewMode ? (
                    // In preview mode, show non-clickable category items
                    <div className="px-6 py-3 text-base font-medium text-foreground/70 cursor-default">
                      {category.name}
                    </div>
                  ) : category.children?.length ? (
                    <div>
                      <button
                        onClick={() => toggleCategory(category.id)}
                        className="flex items-center justify-between w-full px-6 py-3 text-base font-medium text-foreground hover:bg-muted transition-colors"
                      >
                        {category.name}
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform duration-200',
                            expandedCategories.includes(category.id) && 'rotate-180'
                          )}
                        />
                      </button>
                      {expandedCategories.includes(category.id) && (
                        <ul className="bg-muted/50 py-2">
                          {category.children.map((subCategory) => (
                            <li key={subCategory.id}>
                              <SheetClose asChild>
                                <Link
                                  to={`/categories/${subCategory.id}`}
                                  className="block px-10 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  {subCategory.name}
                                </Link>
                              </SheetClose>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <SheetClose asChild>
                      <Link
                        to={`/categories/${category.id}`}
                        className="block px-6 py-3 text-base font-medium text-foreground hover:bg-muted transition-colors"
                      >
                        {category.name}
                      </Link>
                    </SheetClose>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Footer Links */}
          <div className="border-t p-6 space-y-4">
            <SheetClose asChild>
              <Link
                to="/wishlist"
                className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Heart className="h-5 w-5" strokeWidth={1.5} />
                Wishlist
              </Link>
            </SheetClose>
            <SheetClose asChild>
              <Link
                to="/profile"
                className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <User className="h-5 w-5" strokeWidth={1.5} />
                Account
              </Link>
            </SheetClose>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Main NavBar Component
export const NavBar = ({ categories = [] }: NavBarProps) => {
  return <NavBarClient categories={categories} />;
};

// Client-side NavBar wrapper
const NavBarClient = ({ categories: accountCategories }: { categories: Category[] }) => {
  const { categories: presetCategories } = usePresetCategories();
  const [scrolled, setScrolled] = useState(false);

  // Use preset categories if available, otherwise fall back to account categories
  const categories = presetCategories || accountCategories;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          header[data-theme-navbar] a:not([class*="bg-"]),
          header[data-theme-navbar] button:not([class*="bg-"]),
          header[data-theme-navbar] span:not([class*="bg-"]):not([class*="rounded-full"]) {
            color: hsl(var(--navbar-foreground)) !important;
          }
          header[data-theme-navbar] a:hover,
          header[data-theme-navbar] button:hover {
            opacity: 0.8;
          }
        `,
        }}
      />
      <header
        data-theme-navbar
        style={{
          backgroundColor: `hsl(var(--navbar-background))`,
          color: `hsl(var(--navbar-foreground))`,
        }}
        className={cn(
          'sticky top-0 z-50 w-full transition-shadow duration-200',
          scrolled ? 'shadow-soft' : 'border-b border-border'
        )}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Left: Mobile Menu + Logo */}
            <div className="flex items-center gap-4">
              <MobileNav categories={categories} />
              <Logo />
            </div>

            {/* Center: Desktop Navigation */}
            <DesktopNav categories={categories} />

            {/* Right: Actions */}
            <div className="flex items-center gap-1">
              <SearchDrawer categories={categories} />
              <div className="hidden sm:block">
                <UserMenu />
              </div>
              <WishlistButton />
              <CartDrawer />
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export { Logo };
